
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { LanguageClientComponent } from '../../../../components/form/language-client/language-client.component';
import { SwitchFieldComponent } from '../../../../components/form/switch-field/switch-field.component';
import { SelectOption } from '../../../../components/form/select-field/select-field.config';
import { CustomerService } from '../../../../services/api/customer.service';
import { ComplaintsService } from '../../../../services/api/complaints.service';
import { AuthService } from '../../../../services/api/auth.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AlertInfo } from '../../../../models/alert-info';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { OptionService } from '../../../../services/api/option.service';
import { LoaderService } from '../../../../services/loader.service';
import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';

interface ComplaintFile {
  name: string;
  file: File | null;
  attachmentId?: number;
  url?: string;
  isExisting?: boolean;
}

const params = new HttpParams().set('size', '9999');
@Component({
  selector: 'complaints-form',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent,
    TextAreaFieldComponent,
    LanguageClientComponent,
    SwitchFieldComponent,
    NgbAlert,
    LoaderComponent
],
  templateUrl: './complaints-form.component.html',
  styleUrl: './complaints-form.component.scss'
})
export class ComplaintsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() complaint: any;
  @Input() complaintId?: number;
  @Input() isEditPage: boolean = false;
  @Input() complaintStatus?: string;
  @Output() onSavedData = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();
  @Output() onSaveClick = new EventEmitter<void>();
  @Output() onTakingCareChange = new EventEmitter<boolean>();

  form!: FormGroup;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  currentUser: any;
  isCustomerProfile: boolean = false;
  uploadedFiles: ComplaintFile[] = [];
  isTakingCare: boolean = false;
  isTakingCareSaved: boolean = false;
  assignedTo: string = '';
  isLoadingFiles: boolean = false;
  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };
  private readonly LOADER_CONTEXT = 'complaints-form';

  customerName: string = '';
  customerPersonName: string = '';
  languageName: string = '';

  private destroy$ = new Subject<void>();

  categoryOptions: SelectOption[] = [];
  priorityOptions: SelectOption[] = [];
  statusOptions: SelectOption[] = [];

  constructor(
    private fb: FormBuilder,
    protected customerService: CustomerService,
    private complaintsService: ComplaintsService,
    private authService: AuthService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private optionService: OptionService,
    private loaderService: LoaderService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.checkUserProfile();
    this.loadCurrentUser();
    this.loadOptions();
    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOptions(): void {
    this.optionService.complaintPriorities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const mappedOptions = response?.data?.options?.map((option: any) => ({
            key: option.key,
            value: option.value
          })) || [];
          
          this.priorityOptions = [
            { key: '', value: ' ' },
            ...mappedOptions
          ];
        },
        error: (error) => {
          console.error('Error loading priorities:', error);
          this.priorityOptions = [{ key: '', value: ' ' }];
        }
      });

    this.optionService.complaintStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const mappedOptions = response?.data?.options?.map((option: any) => ({
            key: option.key,
            value: option.value
          })) || [];
          
          this.statusOptions = [
            { key: '', value: ' ' },
            ...mappedOptions
          ];
          if (!this.isEditPage && !this.complaintId) {
            const openOption = mappedOptions.find((opt: any) => 
              opt.key.toLowerCase() === 'open' || opt.value.toLowerCase() === 'open'
            );
            if (openOption) {
              this.form.patchValue({ status: openOption.key });
            }
          }
        },
        error: (error) => {
          console.error('Error loading statuses:', error);
          this.statusOptions = [{ key: '', value: ' ' }];
        }
      });

    this.complaintsService.optionsCategories({ params: params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const mappedOptions = response?.data?.complaintCategories?.map((option: any) => ({
            key: option.complaintCategoryId,
            value: option.name
          })) || [];
          
          this.categoryOptions = [
            { key: '', value: ' ' },
            ...mappedOptions
          ];
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          this.categoryOptions = [{ key: '', value: ' ' }];
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['complaint'] && changes['complaint'].currentValue) {
      this.populateForm(changes['complaint'].currentValue);
    }
    if (changes['complaintId'] && changes['complaintId'].currentValue) {
      this.loadAttachments();
    }
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      complaintCategoryId: ['', Validators.required],
      priority: ['', Validators.required],
      status: [{ value: 'open', disabled: true }],
      customerId: ['',],
      customerPersonId: ['', Validators.required],
      languageId: [''],
      summary: ['', Validators.required],
      description: ['', Validators.required],
      tenantId: [1]
    });
  }

  private loadCurrentUser(): void {
    let customerPersonId: number | null = null;
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        customerPersonId = user?.currentUserProfile?.customerPerson?.customerPersonId || null;
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }

    this.customerService.me()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.customer) {
            const customer = response.data.customer;
            this.currentUser = customer.decisionMaker?.user;
            
            if (!this.isEditPage && !this.complaintId) {
              const customerData: any = {
                customerId: customer.customerId || '',
                customerPersonId: customerPersonId || '',
                languageId: customer.language || '' 
              };
              
              this.customerName = customer.companyName || '';
              this.customerPersonName = customer.decisionMaker 
                ? `${customer.decisionMaker.firstName || ''} ${customer.decisionMaker.lastName || ''}`.trim() 
                : '';
              this.languageName = customer.language?.iso2 || customer.country?.iso2 || '';
            
              this.form.patchValue(customerData);
            }
          }
        },
        error: (error) => {
          console.error('Error loading customer data:', error);
        }
      });
  }

  private checkUserProfile(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      const customerProfiles = ['CUS1', 'CUS2', 'CUS3'];
      this.isCustomerProfile = profileCode ? customerProfiles.includes(profileCode) : false;
    });
  }

  private populateForm(complaint: any): void {
    if (complaint) {
      console.log(complaint);
      
      const customerId = complaint.customerPerson?.customer?.customerId || complaint.customerId || complaint.from?.customerId || '';
      const customerPersonId = complaint.customerPerson?.customerPersonId || complaint.customerPersonId || complaint.from?.customerPersonId || '';
      const languageId = complaint.customerPerson?.customer?.language || complaint.language || '';
      
      this.form.patchValue({
        complaintCategoryId: complaint.complaintCategory.complaintCategoryId || '',
        priority: complaint.priority || '',
        status: complaint.status || 'open',
        customerId: customerId,
        customerPersonId: customerPersonId,
        languageId: languageId,
        summary: complaint.summary || complaint.title || '',
        description: complaint.description || ''
      });
      console.log(this.form.value);
      
      
      this.customerName = complaint.customerPerson?.customer?.companyName 
        || complaint.customer?.companyName 
        || complaint.customerName 
        || complaint.from?.name 
        || '';
      
      this.customerPersonName = (complaint.customerPerson?.firstName && complaint.customerPerson?.lastName)
        ? `${complaint.customerPerson.firstName} ${complaint.customerPerson.lastName}`.trim()
        : complaint.customerPersonName || complaint.from?.person || '';
      
      this.languageName = complaint.customerPerson?.customer?.language?.iso2 
        || '';

      if (complaint.takingCareUser) {
        this.isTakingCare = true;
        this.isTakingCareSaved = true;
        const firstName = complaint.takingCareUser.firstName || '';
        const lastName = complaint.takingCareUser.lastName || '';
        this.assignedTo = `${firstName} ${lastName}`.trim();
      } else {
        this.isTakingCare = false;
        this.isTakingCareSaved = false;
        this.assignedTo = '';
      }
      
      if (this.complaintId) {
        this.loadAttachments();
      }
    }
  }

  getFormControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  isFieldDisabled(fieldName: string): boolean {
    return ['customerId', 'customerPersonId', 'languageId'].includes(fieldName);
  }

  handleBack(): void {
    this.onBack.emit();
  }

  handleSave(): void {
    this.onSaveClick.emit();
  }

  loadAttachments(): void {
    if (!this.complaintId) return;

    this.isLoadingFiles = true;
    this.complaintsService.attachmentsComplaintList(this.complaintId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.data?.attachments) {
            this.uploadedFiles = response.data.attachments.map((att: any) => ({
              name: att.fileNameOriginal || att.fileName || att.name,
              file: null,
              attachmentId: att.attachmentId,
              url: att.url,
              isExisting: true
            }));
          }
          this.isLoadingFiles = false;
        },
        error: (error: any) => {
          console.error('Error loading attachments:', error);
          this.isLoadingFiles = false;
        }
      });
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!this.uploadedFiles.find(f => f.name === file.name)) {
          this.uploadedFiles.push({ 
            name: file.name, 
            file: file,
            isExisting: false
          });
        }
      }
    }
    event.target.value = '';
  }

  confirmRemoveFile(index: number): void {
    const file = this.uploadedFiles[index];
    this.confirmationDialogService.open(
      'Are you sure you want to delete this file?',
      () => this.removeFile(index, file)
    );
  }

  downloadFile(fileItem: ComplaintFile): void {
    if (!fileItem.url) return;

    this.complaintsService.GET(fileItem.url)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        },
        error: (error: any) => {
          console.error('Error downloading file:', error);
          this.alertNotificationService.error('Failed to download file');
        }
      });
  }

  removeFile(index: number, file: ComplaintFile): void {
    if (file.isExisting && file.attachmentId && this.complaintId) {
      this.complaintsService.attachmentsComplaintDelete(this.complaintId, file.attachmentId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.uploadedFiles.splice(index, 1);
            this.alertNotificationService.success('File deleted successfully');
          },
          error: (error: any) => {
            console.error('Error deleting file:', error);
            this.alertNotificationService.error('Failed to delete file');
          }
        });
    } else {
      this.uploadedFiles.splice(index, 1);
    }
  }

  uploadNewFiles(complaintId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const newFiles = this.uploadedFiles.filter(f => !f.isExisting && f.file);
      
      if (newFiles.length === 0) {
        resolve();
        return;
      }

      this.loaderService.startLoading(this.LOADER_CONTEXT);

      let uploadedCount = 0;
      let hasError = false;

      newFiles.forEach((fileItem, index) => {
        if (!fileItem.file) return;

        const formData = new FormData();
        formData.append('file', fileItem.file, fileItem.file.name);

        this.complaintsService.attachmentsComplaintCreate(complaintId, formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response: any) => {
              uploadedCount++;
              
              if (response.data?.attachment) {
                const uploadedFileIndex = this.uploadedFiles.findIndex(f => f.name === fileItem.name && !f.isExisting);
                if (uploadedFileIndex !== -1) {
                  this.uploadedFiles[uploadedFileIndex] = {
                    name: response.data.attachment.fileNameOriginal || fileItem.name,
                    file: null,
                    attachmentId: response.data.attachment.attachmentId,
                    url: response.data.attachment.url,
                    isExisting: true
                  };
                }
              }

              if (uploadedCount === newFiles.length) {
                this.loaderService.stopLoading(this.LOADER_CONTEXT);
                if (hasError) {
                  reject(new Error('Some files failed to upload'));
                } else {
                  this.alertNotificationService.success(`${uploadedCount} file(s) uploaded successfully`);
                  resolve();
                }
              }
            },
            error: (error: any) => {
              console.error('Error uploading file:', error);
              hasError = true;
              uploadedCount++;

              if (uploadedCount === newFiles.length) {
                this.loaderService.stopLoading(this.LOADER_CONTEXT);
                this.alertNotificationService.error('Failed to upload some files');
                reject(error);
              }
            }
          });
      });
    });
  }
  
  hasNewFilesToUpload(): boolean {
    return this.uploadedFiles.some(f => !f.isExisting && f.file);
  }

  onTakingCareChanged(event: any): void {
    this.isTakingCare = event;
    
    let loggedUser: any = null;
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        loggedUser = JSON.parse(userData);
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
    
    if (loggedUser) {
      if (this.isTakingCare) {
        const firstName = loggedUser.firstName || '';
        const lastName = loggedUser.lastName || '';
        this.assignedTo = `${firstName} ${lastName}`.trim();
      } else {
        this.assignedTo = '';
      }
    }
    
    this.onTakingCareChange.emit(this.isTakingCare);
  }
  
  getTakingCareUserId(): number | null {
    if (!this.isTakingCare) {
      return null;
    }
    
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const loggedUser = JSON.parse(userData);
        return loggedUser.userId || null;
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
    
    return null;
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
}
